import type {
  DictsysGermanArticleInflectionScheme,
  DictsysInflectionSchemeCell,
  DictsysInflectionSchemeCellElement,
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeGermanArticleType,
  DictsysInflectionSchemeTable
} from "@/types";

import { getBodyCell } from "./dictsys-inflection-scheme.helper";

interface Table {
  subHeader: DictsysInflectionSchemeCell[];
  nominative: DictsysInflectionSchemeCell[];
  accusative: DictsysInflectionSchemeCell[];
  genitive: DictsysInflectionSchemeCell[];
  dative: DictsysInflectionSchemeCell[];
}

function addRows(
  table: Table,
  header: DictsysInflectionSchemeCellElement,
  nominative: DictsysInflectionSchemeContent[] | undefined,
  accusative: DictsysInflectionSchemeContent[] | undefined,
  genitive: DictsysInflectionSchemeContent[] | undefined,
  dative: DictsysInflectionSchemeContent[] | undefined
): Table {
  if (!nominative || !accusative || !genitive || !dative) {
    return table;
  }

  table.subHeader.push({ type: "sub-header", elements: [header] });
  table.nominative.push(getBodyCell(nominative));
  table.accusative.push(getBodyCell(accusative));
  table.genitive.push(getBodyCell(genitive));
  table.dative.push(getBodyCell(dative));

  return table;
}

function getHeader(type: DictsysInflectionSchemeGermanArticleType): string {
  if (type.definite) {
    return "den bestemte artikel";
  }

  if (type.indefinite) {
    return "den ubestemte artikel";
  }

  return "";
}

export function getTable(inflectionScheme: DictsysGermanArticleInflectionScheme): DictsysInflectionSchemeTable | null {
  if (!inflectionScheme.article) {
    return null;
  }

  const article = inflectionScheme.article;
  const type = article.definite || article.indefinite;

  if (!type) {
    return null;
  }

  let table: Table = {
    subHeader: [{ type: "sub-header", elements: [] }],
    nominative: [{ type: "sub-header", elements: [{ texts: ["Nominativ"] }] }],
    accusative: [{ type: "sub-header", elements: [{ texts: ["Akkusativ"] }] }],
    genitive: [{ type: "sub-header", elements: [{ texts: ["Genitiv"] }] }],
    dative: [{ type: "sub-header", elements: [{ texts: ["Dativ"] }] }]
  };

  table = addRows(
    table,
    { texts: ["Femininum"] },
    type.nominative.feminine?.singular,
    type.akkusative.feminine?.singular,
    type.genitive.feminine?.singular,
    type.dative.feminine?.singular
  );

  table = addRows(
    table,
    { texts: ["Maskulinum"] },
    type.nominative.masculine?.singular,
    type.akkusative.masculine?.singular,
    type.genitive.masculine?.singular,
    type.dative.masculine?.singular
  );

  table = addRows(
    table,
    { texts: ["Neutrum"] },
    type.nominative.neutral?.singular,
    type.akkusative.neutral?.singular,
    type.genitive.neutral?.singular,
    type.dative.neutral?.singular
  );

  table = addRows(
    table,
    { texts: ["Pluralis"], comment: "(flertal)" },
    type.nominative.plural,
    type.akkusative.plural,
    type.genitive.plural,
    type.dative.plural
  );

  return {
    header: getHeader(article),
    cells: [table.subHeader, table.nominative, table.accusative, table.genitive, table.dative]
  };
}
